'use client';

// Analytics provider component.
//
// Behaviour:
//   - LOCAL DEV (`NODE_ENV !== 'production'`): noop provider. No Statsig
//     init, no events fired. Engineers see clean local logs and Statsig
//     dashboards stay free of dev noise. Feature gates fall through to
//     local defaults / DevToolkit overrides.
//
//   - VERCEL BUILDS: Statsig is initialised with environment 'production'
//     for BOTH devnet and mainnet — they share the same flag state at
//     parity (per the user's spec: "devnet and mainnet should have the
//     same feature flag state"). Network-specific segmentation happens
//     via the `network` custom user property, which `setAnalyticsNetwork`
//     refreshes on every toggle.
import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect } from 'react';
import { markProviderReady, setAnalyticsNetwork, setProvider } from '@/lib/analytics';
import { createCompositeProvider } from '@/lib/analytics/composite';
import { createNoopProvider } from '@/lib/analytics/noop';
import { createCustomerIoProvider } from '@/lib/analytics/providers/customerio';
import { createStatsigProvider } from '@/lib/analytics/providers/statsig';
import { env } from '@/lib/config/env';
import { useNetworkStore } from '@/lib/network/store';
import { logger } from '@/lib/telemetry/logger';
import { MainnetGateMirror } from './mainnet-gate-mirror';
interface AnalyticsProviderProps {
  children: ReactNode;
  clientKey?: string | undefined;
}
const IS_PRODUCTION_BUILD: boolean = process.env.NODE_ENV === 'production';
export function AnalyticsProvider(t0) {
  const $ = _c(9);
  const {
    children,
    clientKey
  } = t0;
  const network = useNetworkStore(_temp);
  let t1;
  let t2;
  if ($[0] !== clientKey) {
    t1 = () => {
      const customerIoKey = env.NEXT_PUBLIC_CUSTOMERIO_CDP_WRITE_KEY;
      const wantStatsig = IS_PRODUCTION_BUILD && clientKey !== undefined && clientKey !== "";
      const wantCustomerIo = IS_PRODUCTION_BUILD && customerIoKey !== undefined && customerIoKey !== "";
      if (!wantStatsig && !wantCustomerIo) {
        setProvider(createNoopProvider());
        markProviderReady();
        return;
      }
      const primary = wantStatsig ? createStatsigProvider() : createNoopProvider();
      const sinks = [...(wantCustomerIo && customerIoKey !== undefined ? [createCustomerIoProvider(customerIoKey)] : [])];
      const installed = sinks.length > 0 ? createCompositeProvider(primary, sinks) : primary;
      setProvider(installed);
      if (wantStatsig && clientKey !== undefined) {
        installed.initialize({
          clientKey,
          environment: "production"
        }).then(_temp2).catch(_temp3);
      } else {
        installed.initialize({
          clientKey: "",
          environment: "production"
        });
        markProviderReady();
      }
      return () => {
        installed.shutdown();
      };
    };
    t2 = [clientKey];
    $[0] = clientKey;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== network) {
    t3 = () => {
      setAnalyticsNetwork(network);
    };
    t4 = [network];
    $[3] = network;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <MainnetGateMirror />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== children) {
    t6 = <>{t5}{children}</>;
    $[7] = children;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
}
function _temp3(err) {
  logger.warn("Statsig init failed \u2014 marking analytics provider ready anyway", {
    error: err instanceof Error ? err.message.slice(0, 200) : String(err)
  });
  markProviderReady();
}
function _temp2() {
  markProviderReady();
}
function _temp(s) {
  return s.current;
}